import type { ComponentPropsWithoutRef } from 'react';
import styles from './skeleton.module.css';

type SkeletonVariant = 'text' | 'textSm' | 'heading' | 'avatar' | 'button' | 'thumb';

type SkeletonProps = ComponentPropsWithoutRef<'div'> & {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
};

/**
 * Individual skeleton placeholder.
 * aria-hidden by default — wrap multiple skeletons in a single
 * <div role="status" aria-label="Loading…"> container instead.
 */
export function Skeleton({
  variant,
  width,
  height,
  style,
  className,
  ...props
}: SkeletonProps) {
  const classes = [
    styles.skeleton,
    variant ? styles[variant] : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      aria-hidden="true"
      className={classes}
      style={{
        width: width !== undefined ? (typeof width === 'number' ? `${width}px` : width) : undefined,
        height:
          height !== undefined ? (typeof height === 'number' ? `${height}px` : height) : undefined,
        ...style,
      }}
      {...props}
    />
  );
}

/**
 * Wraps a group of Skeleton placeholders with a single live-region announcement.
 * Screen readers announce "Loading…" once for the whole group.
 */
export function SkeletonGroup({
  label = 'Loading…',
  children,
  ...props
}: ComponentPropsWithoutRef<'div'> & { label?: string }) {
  return (
    <div role="status" aria-label={label} {...props}>
      {children}
    </div>
  );
}
